import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSocket } from 'utils/socketManager';
import { getToken } from 'utils/crypto';
import { aiAssistantService } from './aiAssistant.service';
import {
    AI_ASSISTANT_SOCKET_EVENT,
    AgentErrorCode,
    AgentEventType,
    AiGovernanceErrorCode,
    AssistantCopy,
    BUSY_RETRY_MS,
    MessageRole,
    MessageStatus,
    RECONNECT_GRACE_MS,
    SESSION_IDLE_MS,
    TURN_STALL_TIMEOUT_MS,
    TurnActivity,
} from './aiAssistant.constants';

let nextId = 0;
const localId = () => `m${Date.now()}-${nextId++}`;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const browserContext = () => ({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
});

/** Copy for an admin AI-policy refusal; the server's message wins (it names the feature and the reset). */
const GOVERNANCE_FALLBACK = {
    [AiGovernanceErrorCode.AI_DISABLED]: AssistantCopy.AI_DISABLED,
    [AiGovernanceErrorCode.ASSISTANT_DISABLED]: AssistantCopy.ASSISTANT_DISABLED,
    [AiGovernanceErrorCode.LIMIT_REACHED]: AssistantCopy.LIMIT_REACHED,
    [AiGovernanceErrorCode.NOT_CONFIGURED]: AssistantCopy.NOT_CONFIGURED,
};

/**
 * The app's axios interceptor (utils/hooks/axios.js) rethrows `{ status, data }`, not the axios error,
 * so `err.response` is undefined in practice; both shapes are read.
 */
const errorStatus = (err) => err?.status ?? err?.response?.status;
const errorData = (err) => err?.data ?? err?.response?.data;

const httpErrorMessage = (err) => {
    const status = errorStatus(err);
    const data = errorData(err);
    const governance = GOVERNANCE_FALLBACK[data?.code];
    if (governance) return typeof data.message === 'string' && data.message ? data.message : governance;
    if (status === 403) return AssistantCopy.DISABLED;
    // Another turn of this user's (e.g. in another tab) is still running.
    if (status === 409) return AssistantCopy.BUSY;
    if (status === 429) return AssistantCopy.TOO_MANY_REQUESTS;
    return AssistantCopy.FAILED;
};

/**
 * Chat state for one ephemeral assistant session.
 *
 * Deliberately local state, not Redux: the conversation is throwaway by design
 * and must not linger in the global store. The server holds history, so each
 * send carries only the new message; "New chat", logout and closing the tab
 * all discard the session on both sides.
 *
 * At most one turn is in flight. Its events are matched by turnId. Until the POST
 * returns that id, events for this session are held, not shown: they could come
 * from an older turn of the same session still finishing on the server (one this
 * screen gave up on), and only the id tells them apart. A stall timer recovers the
 * UI if a turn never finishes.
 *
 * Every chat has a generation (`genRef`), bumped by "New chat": a request that
 * started for an older chat never writes into the current one. The server
 * allows one running turn per user, so a message sent while an older turn is
 * still finishing (after "New chat", or from another tab) waits for it instead
 * of failing. Event `sequence` numbers reveal a reply that lost part of its
 * text to a socket drop.
 */
export const useAssistantChat = () => {
    const [messages, setMessages] = useState([]);
    const [isBusy, setIsBusy] = useState(false);
    const sessionIdRef = useRef(null);
    const turnRef = useRef(null); // { messageId, turnId, pending, timer, sequence, gap, offline }
    const genRef = useRef(0);
    /** When the server last saved this session (a completed turn) — its idle TTL counts from there. 0 = nothing saved. */
    const lastSavedAtRef = useRef(0);

    const patchMessage = useCallback((id, patch) => {
        setMessages((list) => list.map((m) => (m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m)));
    }, []);

    const finishTurn = useCallback(
        (patch) => {
            const turn = turnRef.current;
            if (!turn) return;
            clearTimeout(turn.timer);
            turnRef.current = null;
            patchMessage(turn.messageId, { activity: null, ...patch });
            setIsBusy(false);
        },
        [patchMessage],
    );

    const armTimer = useCallback(
        (ms, patch) => {
            const turn = turnRef.current;
            if (!turn) return;
            clearTimeout(turn.timer);
            turn.timer = setTimeout(() => finishTurn(patch), ms);
        },
        [finishTurn],
    );

    const armStallTimer = useCallback(
        () => armTimer(TURN_STALL_TIMEOUT_MS, { status: MessageStatus.ERROR, error: AssistantCopy.STALLED }),
        [armTimer],
    );

    /** Applies one event of the current turn to its reply. */
    const applyEvent = useCallback(
        (turn, event) => {
            armStallTimer();

            // A skipped number means events were lost (socket drop) — the text has a hole in it.
            if (typeof event.sequence === 'number') {
                if (event.sequence !== turn.sequence + 1) turn.gap = true;
                turn.sequence = event.sequence;
            }

            switch (event.type) {
                case AgentEventType.TEXT_DELTA:
                    patchMessage(turn.messageId, (m) => ({ text: m.text + event.text }));
                    break;
                case AgentEventType.TOOL_STARTED:
                    patchMessage(turn.messageId, { activity: event.tool });
                    break;
                case AgentEventType.TOOL_FINISHED:
                    patchMessage(turn.messageId, { activity: null });
                    break;
                case AgentEventType.TURN_COMPLETED:
                    lastSavedAtRef.current = Date.now();
                    // The server saved the whole answer, but what reached this screen has a hole in it.
                    finishTurn(
                        turn.gap
                            ? { status: MessageStatus.ERROR, error: AssistantCopy.CONNECTION_LOST }
                            : { status: MessageStatus.DONE },
                    );
                    break;
                case AgentEventType.TURN_FAILED:
                    finishTurn({
                        status: MessageStatus.ERROR,
                        error: event.message || (event.code === AgentErrorCode.NOT_CONFIGURED ? AssistantCopy.NOT_CONFIGURED : AssistantCopy.FAILED),
                        errorCode: event.code,
                    });
                    break;
                default:
                    break;
            }
        },
        [armStallTimer, finishTurn, patchMessage],
    );

    useEffect(() => {
        const socket = getSocket(getToken(), localStorage.getItem('deviceId') || '');
        if (!socket) return undefined;

        const onEvent = (event) => {
            const turn = turnRef.current;
            if (!turn || event?.sessionId !== sessionIdRef.current) return;
            if (!turn.turnId) {
                turn.pending.push(event); // sorted out once the POST names this turn
                return;
            }
            if (event.turnId === turn.turnId) applyEvent(turn, event);
        };

        // Say so at once instead of a silent wait; events sent while offline are gone for good.
        const onDisconnect = () => {
            const turn = turnRef.current;
            if (!turn) return;
            turn.offline = true;
            patchMessage(turn.messageId, { activity: TurnActivity.RECONNECTING });
        };

        // Back online: the reply either continues (its next event re-arms the stall timer and its
        // sequence shows whether anything was missed) or it already ended while we were away.
        const onConnect = () => {
            const turn = turnRef.current;
            if (!turn?.offline) return;
            turn.offline = false;
            patchMessage(turn.messageId, { activity: null });
            armTimer(RECONNECT_GRACE_MS, { status: MessageStatus.ERROR, error: AssistantCopy.CONNECTION_LOST });
        };

        socket.on(AI_ASSISTANT_SOCKET_EVENT, onEvent);
        socket.on('disconnect', onDisconnect);
        socket.on('connect', onConnect);
        return () => {
            socket.off(AI_ASSISTANT_SOCKET_EVENT, onEvent);
            socket.off('disconnect', onDisconnect);
            socket.off('connect', onConnect);
        };
    }, [applyEvent, armTimer, patchMessage]);

    /** The POST named this turn: apply its held events, drop any from an older turn of the session. */
    const adoptTurnId = useCallback(
        (turn, turnId) => {
            turn.turnId = turnId;
            const held = turn.pending;
            turn.pending = [];
            for (const event of held) {
                if (turnRef.current !== turn) break; // a held terminal event already ended it
                if (event.turnId === turnId) applyEvent(turn, event);
            }
        },
        [applyEvent],
    );

    /**
     * Posts the message. While the user's previous turn still runs on the server (409 — after
     * "New chat" mid-reply, or from another tab) it waits and retries rather than failing; the
     * stall timer bounds the wait, and a reset of this chat ends it.
     */
    const postMessage = useCallback(
        async (sessionId, body, messageId, isCurrent) => {
            for (;;) {
                try {
                    const response = await aiAssistantService.sendMessage(sessionId, body);
                    if (isCurrent()) patchMessage(messageId, { activity: null });
                    return response;
                } catch (err) {
                    if (errorStatus(err) !== 409 || !isCurrent()) throw err;
                    patchMessage(messageId, { activity: TurnActivity.WAITING });
                    await sleep(BUSY_RETRY_MS);
                    if (!isCurrent()) throw err;
                }
            }
        },
        [patchMessage],
    );

    const send = useCallback(
        async (raw, page) => {
            const text = raw.trim();
            if (!text || turnRef.current) return;

            // The server has forgotten an idle session: start a new one and say so, rather than let the
            // model answer without the conversation the user still sees above.
            const expired = lastSavedAtRef.current && Date.now() - lastSavedAtRef.current > SESSION_IDLE_MS;
            if (expired) {
                const stale = sessionIdRef.current;
                sessionIdRef.current = null;
                lastSavedAtRef.current = 0;
                if (stale) aiAssistantService.closeSession(stale).catch(() => {});
            }

            const gen = genRef.current;
            const reply = { id: localId(), role: MessageRole.ASSISTANT, text: '', status: MessageStatus.STREAMING };
            const isCurrent = () => genRef.current === gen && turnRef.current?.messageId === reply.id;

            setMessages((list) => [
                ...list,
                ...(expired ? [{ id: localId(), role: MessageRole.NOTICE, text: AssistantCopy.SESSION_EXPIRED }] : []),
                { id: localId(), role: MessageRole.USER, text, status: MessageStatus.DONE },
                reply,
            ]);
            setIsBusy(true);
            turnRef.current = { messageId: reply.id, turnId: null, pending: [], timer: null, sequence: -1, gap: false, offline: false };
            armStallTimer();

            try {
                let sessionId = sessionIdRef.current;
                if (!sessionId) {
                    const { data } = await aiAssistantService.openSession();
                    // "New chat" while opening: this id belongs to no chat (and has no server state yet).
                    if (genRef.current !== gen) return;
                    sessionId = data.sessionId;
                    sessionIdRef.current = sessionId;
                }
                const { data } = await postMessage(sessionId, { message: text, page, ...browserContext() }, reply.id, isCurrent);
                // Reset meanwhile: that reset already closed this session; the turn's events are ignored.
                if (isCurrent()) adoptTurnId(turnRef.current, data.turnId);
            } catch (err) {
                if (isCurrent()) finishTurn({ status: MessageStatus.ERROR, error: httpErrorMessage(err) });
            }
        },
        [adoptTurnId, armStallTimer, finishTurn, postMessage],
    );

    /**
     * Drops the session on the server and starts over — also mid-reply: the running turn is
     * detached (its events no longer match), and the next message waits for it to finish.
     */
    const reset = useCallback(() => {
        const sessionId = sessionIdRef.current;
        genRef.current += 1;
        sessionIdRef.current = null;
        lastSavedAtRef.current = 0;
        if (turnRef.current) clearTimeout(turnRef.current.timer);
        turnRef.current = null;
        setIsBusy(false);
        setMessages([]);
        if (sessionId) aiAssistantService.closeSession(sessionId).catch(() => {});
    }, []);

    // Read through a ref, so `retry` stays the same function while a reply streams (no list-wide re-render).
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    /** Re-asks the question behind a failed reply. */
    const retry = useCallback(
        (failedId, page) => {
            const list = messagesRef.current;
            const index = list.findIndex((m) => m.id === failedId);
            const question = list[index - 1];
            if (index < 1 || question?.role !== MessageRole.USER || turnRef.current) return;
            setMessages((current) => current.filter((m) => m.id !== failedId && m.id !== question.id));
            send(question.text, page);
        },
        [send],
    );

    /** Today's remaining share of assistant use, as a whole percent; `null` until loaded or when it can't be read. */
    const [quota, setQuota] = useState(null);
    const refreshQuota = useCallback(() => {
        aiAssistantService
            .getQuota()
            .then(({ data }) => setQuota(typeof data?.dailyRemainingPercent === 'number' ? data.dailyRemainingPercent : null))
            .catch(() => setQuota(null));
    }, []);

    // A turn that just ended changed what's left. (The panel also refreshes on open.)
    const wasBusyRef = useRef(false);
    useEffect(() => {
        if (wasBusyRef.current && !isBusy) refreshQuota();
        wasBusyRef.current = isBusy;
    }, [isBusy, refreshQuota]);

    // Closing the tab or signing out ends the session server-side too.
    useEffect(() => {
        const onPageHide = (event) => {
            // `persisted`: the page went into the back/forward cache and may come back with the chat on
            // screen — keep its session; the idle TTL removes it if the page never returns.
            if (event.persisted || !sessionIdRef.current) return;
            aiAssistantService.closeSessionOnUnload(sessionIdRef.current);
        };
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
            if (turnRef.current) clearTimeout(turnRef.current.timer);
            if (sessionIdRef.current) aiAssistantService.closeSessionOnUnload(sessionIdRef.current);
        };
    }, []);

    return useMemo(
        () => ({ messages, isBusy, send, reset, retry, quota, refreshQuota }),
        [messages, isBusy, send, reset, retry, quota, refreshQuota],
    );
};
